import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useHistory, useParams } from 'react-router-dom'
import {
    MdStar, MdSchool, MdCheckCircle, MdArrowForward,
    MdComputer, MdLocalHospital, MdBusiness, MdTrendingUp, MdSettings,
    MdBuild, MdLanguage, MdCardMembership, MdVerifiedUser, MdFlightTakeoff, MdPeople,
    MdAssessment, MdPlayCircleOutline, MdAccessTime, MdPeopleOutline,
    MdLocationOn, MdEmail, MdPhone, MdChat
} from 'react-icons/md'
import { Button, Divider, Typography, message } from 'antd'
import PublicPageLayout from '../../components/PublicPageLayout'
import courseService from '../../services/courses'
import { getAllCourses } from '../../reducers/courseReducer'
import Spinner from '../../components/Spinner'

const { Title, Paragraph, Text } = Typography

/* ── SHARED INNER STYLES ── */
const PageBg = styled.div`background: #f4f7f9; min-height: 80vh;`
const Wrap = styled.div`max-width: 1200px; margin: 0 auto; padding: 48px 16px;`


const PageHero = styled.div`
  position: relative;
  border-radius: 12px;
  padding: 70px 56px;
  margin-bottom: 40px;
  overflow: hidden;
  background: ${p => p.bg ? `url(${p.bg}) center/cover no-repeat` : 'white'};
  border: ${p => p.bg ? 'none' : '1px solid #e5e7eb'};
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${p => p.bg && `
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(10,20,60,0.82) 0%, rgba(10,20,60,0.55) 60%, rgba(10,20,60,0.2) 100%);
      border-radius: 12px;
    }
  `}

  > * { position: relative; z-index: 1; }

  .badge {
    display: inline-flex; align-items: center;
    background: ${p => p.bg ? 'rgba(255,255,255,0.18)' : '#e8f5e9'};
    color: ${p => p.bg ? 'white' : '#2e7d32'};
    border: ${p => p.bg ? '1px solid rgba(255,255,255,0.35)' : 'none'};
    font-size: 12px; font-weight: 700; padding: 4px 14px; border-radius: 20px;
    text-transform: uppercase; letter-spacing: 1px; margin-bottom: 16px;
    backdrop-filter: ${p => p.bg ? 'blur(6px)' : 'none'};
  }
  h1 {
    font-size: 2.6rem; font-weight: 900;
    color: ${p => p.bg ? 'white' : '#111827'};
    margin-bottom: 16px; line-height: 1.15;
    max-width: 600px;
    text-shadow: ${p => p.bg ? '0 2px 12px rgba(0,0,0,0.4)' : 'none'};
  }
  p {
    font-size: 1.05rem;
    color: ${p => p.bg ? 'rgba(255,255,255,0.88)' : '#4b5563'};
    line-height: 1.7; max-width: 580px; margin-bottom: 28px;
  }
  .cta-row { display: flex; gap: 16px; flex-wrap: wrap; }
  .btn-primary {
    height: 50px; padding: 0 32px; background: hsl(33, 100%, 50%); border: none;
    border-radius: 6px; color: white; font-size: 15px; font-weight: 700; cursor: pointer;
    box-shadow: none;
    &:hover { opacity: 0.9; transform: translateY(-1px); }
  }
  .btn-secondary {
    height: 50px; padding: 0 32px;
    background: ${p => p.bg ? 'rgba(255,255,255,0.15)' : 'transparent'};
    border: 2px solid ${p => p.bg ? 'rgba(255,255,255,0.6)' : 'hsl(210,55%,22%)'};
    border-radius: 6px;
    color: ${p => p.bg ? 'white' : 'hsl(210,55%,22%)'};
    font-size: 15px; font-weight: 700; cursor: pointer;
    backdrop-filter: ${p => p.bg ? 'blur(6px)' : 'none'};
    &:hover {
      background: ${p => p.bg ? 'rgba(255,255,255,0.28)' : 'hsl(210,55%,22%)'};
      color: white;
    }
  }

  @media (max-width: 768px) {
    padding: 40px 24px;
    h1 { font-size: 1.8rem; }
    p { font-size: 0.95rem; }
  }
`


const SectionTitle = styled.h2`
  font-size: 22px; font-weight: 800; color: #111827; margin-bottom: 24px;
`

const Grid3 = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 48px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`

const Grid4 = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 48px;
  @media (max-width: 1024px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`

const Card = styled.div`
  background: white; border-radius: 10px; padding: 28px 24px;
  border: 1px solid #e5e7eb; cursor: pointer; transition: all 0.25s;
  &:hover { border-color: hsl(210,55%,22%); box-shadow: 0 8px 24px rgba(0,0,0,0.08); transform: translateY(-4px); }
  .card-icon {
    width: 52px; height: 52px; border-radius: 12px;
    background: ${p => p.bg || '#e8f5e9'}; color: ${p => p.color || '#2e7d32'};
    display: flex; align-items: center; justify-content: center; font-size: 26px; margin-bottom: 18px;
  }
  h4 { font-size: 16px; font-weight: 800; color: #111827; margin-bottom: 8px; }
  p { font-size: 13px; color: #6b7280; line-height: 1.6; margin-bottom: 16px; }
  .link { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: hsl(210,55%,22%); }
`

const FeatCard = styled.div`
  background: white; border-radius: 10px; padding: 32px 24px;
  border: 1px solid #e5e7eb; text-align: center; transition: all 0.25s;
  &:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.07); transform: translateY(-3px); border-color: hsl(33,100%,50%); }
  .feat-icon {
    width: 64px; height: 64px; border-radius: 50%;
    background: #fff8e1; color: hsl(33,100%,50%);
    display: flex; align-items: center; justify-content: center; font-size: 30px;
    margin: 0 auto 20px;
  }
  h4 { font-size: 16px; font-weight: 800; color: #111827; margin-bottom: 10px; }
  p { font-size: 13px; color: #6b7280; line-height: 1.6; margin: 0; }
`

const CourseCard = styled.div`
  background: white; border-radius: 8px; overflow: hidden;
  border: 1px solid #e5e7eb; cursor: pointer; transition: all 0.2s;
  &:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.10); transform: translateY(-3px); }
  .thumb { 
    height: 140px; background: #e2e8f0; overflow: hidden; position: relative;
    img { width: 100%; height: 100%; object-fit: cover; }
    .badge {
      position: absolute; top: 8px; right: 8px; background: rgba(255,255,255,0.9);
      border-radius: 3px; font-size: 10px; font-weight: 800;
      padding: 3px 8px; color: #374151; text-transform: uppercase;
    }
  }
  .body { padding: 14px; }
  .title { font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 10px; line-height: 1.4; }
  .stars { display: flex; align-items: center; gap: 2px; color: #f59e0b; font-size: 13px; margin-bottom: 12px; }
  .btn {
    width: 100%; height: 36px; background: hsl(33,100%,50%); border: none;
    border-radius: 4px; color: white; font-size: 13px; font-weight: 700; cursor: pointer;
    &:hover { opacity: 0.9; }
  }
`

const CtaBanner = styled.div`
  background: hsl(210,55%,22%); border-radius: 12px; padding: 48px;
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 48px; gap: 32px;
  .text { h2 { font-size: 26px; font-weight: 900; color: white; margin-bottom: 10px; } p { color: rgba(255,255,255,0.8); font-size:15px; margin:0; max-width: 500px; } }
  .actions { display: flex; gap: 12px; flex-shrink: 0; flex-wrap: wrap; }
  .btn-white { height: 50px; padding: 0 32px; background: white; border: none; border-radius: 6px; color: hsl(210,55%,22%); font-weight: 800; font-size: 15px; cursor: pointer; &:hover { opacity: 0.9; } }
  .btn-outline { height: 50px; padding: 0 32px; background: transparent; border: 2px solid rgba(255,255,255,0.5); border-radius: 6px; color: white; font-weight: 700; font-size: 15px; cursor: pointer; &:hover { border-color: white; background: rgba(255,255,255,0.1); } }
  @media (max-width: 768px) { flex-direction: column; }
`

const StatsBand = styled.div`
  display: flex; background: white; border: 1px solid #e5e7eb;
  border-radius: 10px; margin-bottom: 40px; overflow: hidden;
  .stat { flex: 1; padding: 22px 24px; border-right: 1px solid #e5e7eb; &:last-child { border-right: none; }
    .num { font-size: 26px; font-weight: 900; color: hsl(210,55%,22%); }
    .lbl { font-size: 12px; color: #6b7280; margin-top: 3px; }
  }
`

const THUMBS = [
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&q=80',
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&q=80',
    'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400&q=80',
    'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&q=80',
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&q=80',
    'https://images.unsplash.com/photo-1543966888-7c1dc482a810?w=400&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80',
    'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&q=80',
]


/* ══════════════════════════════════════════════════════
   PAGE: DIPLOMAS  /diplomas
══════════════════════════════════════════════════════ */
export const DiplomasPage = () => {
    const history = useHistory()
    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(getAllCourses())
    }, [dispatch])

    const { data: allCourses = [], loading } = useSelector(state => state.courses) || {}

    // Only display courses that are Diplomas from Backend
    const diplomaCourses = allCourses?.filter(c => (c.type === 'Diploma' || String(c.name).toLowerCase().includes('diploma')) && c.status !== 'archived') || []
    const displayCourses = diplomaCourses.slice(0, 8)

    return (
        <PublicPageLayout activeLink="courses">
            <PageBg>
                <Wrap>
                    <PageHero bg="/heroes/diplomas.png">
                        <h1>Online Diploma Courses</h1>
                        <p>Earn internationally recognized diplomas that open doors to better jobs and higher salaries. Our diploma programs are designed with industry experts to match real employer needs.</p>
                        <div className="cta-row">
                            <button className="btn-primary" onClick={() => history.push('/explore-courses')}>Browse All Diplomas</button>
                            <button className="btn-secondary" onClick={() => history.push('/register')}>Start Free Today</button>
                        </div>
                    </PageHero>

                    <StatsBand>
                        <div className="stat"><div className="num">200+</div><div className="lbl">Diploma Programs</div></div>
                        <div className="stat"><div className="num">15M+</div><div className="lbl">Graduates</div></div>
                        <div className="stat"><div className="num">100%</div><div className="lbl">Free to Enroll</div></div>
                        <div className="stat"><div className="num">12-30</div><div className="lbl">Weeks Per Diploma</div></div>
                    </StatsBand>

                    <SectionTitle>Featured Diploma Programs</SectionTitle>
                    {loading ? (
                        <div style={{ padding: '60px 0', textAlign: 'center' }}>
                            <Spinner size="large" />
                        </div>
                    ) : (displayCourses.length > 0 ? (
                        <Grid4>
                            {displayCourses.map((c, i) => (
                                <CourseCard key={c._id || c.id} onClick={() => history.push(c._id ? `/course/${c._id}` : '/register')}>
                                    <div className="thumb">
                                        <img src={c.image || c.img || THUMBS[i % THUMBS.length]} alt={c.name || c.title} />
                                    </div>
                                    <div className="body">
                                        <div className="title">{c.name || c.title}</div>
                                        <div className="stars">{[...Array(5)].map((_, si) => <MdStar key={si} />)}</div>
                                        <button className="btn">{c._id ? 'Explore Diploma' : 'Enroll Free'}</button>
                                    </div>
                                </CourseCard>
                            ))}
                        </Grid4>
                    ) : (
                        <div style={{ padding: '40px', textAlign: 'center', background: 'white', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                            <p style={{ margin: 0, fontSize: 16, color: '#666' }}>No active diploma programs found in the catalog at this moment.</p>
                        </div>
                    ))}

                    <CtaBanner>
                        <div className="text">
                            <h2>Start Your Diploma Journey Today</h2>
                            <p>Join millions of learners who have transformed their careers with our free online diploma programs.</p>
                        </div>
                        <div className="actions">
                            <button className="btn-white" onClick={() => history.push('/register')}>Get Started Free</button>
                            <button className="btn-outline" onClick={() => history.push('/explore-courses')}>View All Courses</button>
                        </div>
                    </CtaBanner>
                </Wrap>
            </PageBg>
        </PublicPageLayout>
    )
}

/* ══════════════════════════════════════════════════════
   PAGE: CERTIFICATES  /certificates
══════════════════════════════════════════════════════ */
export const CertificatesPage = () => {
    const history = useHistory()
    const certs = [
        { title: 'IT Support Specialist', bg: '#e3f2fd', color: '#1565c0', icon: <MdComputer /> },
        { title: 'Digital Marketing', bg: '#e8f5e9', color: '#2e7d32', icon: <MdTrendingUp /> },
        { title: 'Business Analytics', bg: '#fff3e0', color: '#e65100', icon: <MdAssessment /> },
        { title: 'Healthcare Management', bg: '#fce4ec', color: '#c62828', icon: <MdLocalHospital /> },
        { title: 'Project Management', bg: '#e8eaf6', color: '#283593', icon: <MdSettings /> },
        { title: 'Teaching & Education', bg: '#f3e5f5', color: '#6a1b9a', icon: <MdSchool /> },
    ]
    return (
        <PublicPageLayout activeLink="courses">
            <PageBg>
                <Wrap>
                    <PageHero bg="/heroes/certificates.png">
                        <h1>Professional Certificate Courses</h1>
                        <p>Earn job-ready certificates in high-demand fields. Our certificates are backed by industry leaders and recognized by top employers worldwide — all completely free to start.</p>
                        <div className="cta-row">
                            <button className="btn-primary" onClick={() => history.push('/explore-courses')}>Browse Certificates</button>
                            <button className="btn-secondary" onClick={() => history.push('/register')}>Create Free Account</button>
                        </div>
                    </PageHero>

                    <StatsBand>
                        <div className="stat"><div className="num">3,000+</div><div className="lbl">Certificates</div></div>
                        <div className="stat"><div className="num">500+</div><div className="lbl">Partner Employers</div></div>
                        <div className="stat"><div className="num">95%</div><div className="lbl">Job Placement Rate</div></div>
                        <div className="stat"><div className="num">6-10hrs</div><div className="lbl">Average Course Length</div></div>
                    </StatsBand>

                    <SectionTitle>Top Certificate Categories</SectionTitle>
                    <Grid3>
                        {certs.map((c, i) => (
                            <Card key={i} bg={c.bg} color={c.color} onClick={() => history.push('/explore-courses')}>
                                <div className="card-icon">{c.icon}</div>
                                <h4>{c.title} Certificate</h4>
                                <p>Gain job-ready skills and earn a recognized certificate to boost your career in {c.title.toLowerCase()}.</p>
                                <span className="link">Explore Courses <MdArrowForward /></span>
                            </Card>
                        ))}
                    </Grid3>

                    <CtaBanner>
                        <div className="text">
                            <h2>Earn Your Certificate Today</h2>
                            <p>Start for free. Learn at your own pace. Get certified and land your dream job.</p>
                        </div>
                        <div className="actions">
                            <button className="btn-white" onClick={() => history.push('/register')}>Start for Free</button>
                        </div>
                    </CtaBanner>
                </Wrap>
            </PageBg>
        </PublicPageLayout>
    )
}

/* ══════════════════════════════════════════════════════
   PAGE: SKILL PATHS  /skill-paths
══════════════════════════════════════════════════════ */
export const SkillPathsPage = () => {
    const history = useHistory()
    const paths = [
        { title: 'Data Science Path', steps: '8 Courses', desc: 'Go from zero to job-ready data scientist with Python, SQL, ML & more.', bg: '#e3f2fd', color: '#1565c0', icon: <MdComputer /> },
        { title: 'Full-Stack Developer', steps: '10 Courses', desc: 'Master HTML, CSS, JavaScript, React and Node.js to build full apps.', bg: '#e8f5e9', color: '#2e7d32', icon: <MdBuild /> },
        { title: 'Digital Marketing', steps: '6 Courses', desc: 'Master SEO, social media, email marketing and paid advertising.', bg: '#fff3e0', color: '#e65100', icon: <MdTrendingUp /> },
        { title: 'Business Leadership', steps: '7 Courses', desc: 'Develop executive leadership, strategy and people management skills.', bg: '#f3e5f5', color: '#6a1b9a', icon: <MdBusiness /> },
        { title: 'Health Professional', steps: '9 Courses', desc: 'Build clinical knowledge in anatomy, pharmacology, and patient care.', bg: '#fce4ec', color: '#c62828', icon: <MdLocalHospital /> },
        { title: 'Language Mastery', steps: '5 Courses', desc: 'Learn English, French or Spanish from beginner to professional level.', bg: '#e8eaf6', color: '#283593', icon: <MdLanguage /> },
    ]
    return (
        <PublicPageLayout activeLink="courses">
            <PageBg>
                <Wrap>
                    <PageHero bg="/heroes/skill-paths.png">
                        <h1>Curated Skill Learning Paths</h1>
                        <p>Don't know where to start? Follow guided skill paths designed by experts to take you from beginner to job-ready in a structured, step-by-step journey.</p>
                        <div className="cta-row">
                            <button className="btn-primary" onClick={() => history.push('/explore-courses')}>Explore Skill Paths</button>
                            <button className="btn-secondary" onClick={() => history.push('/register')}>Get Started Free</button>
                        </div>
                    </PageHero>

                    <StatsBand>
                        <div className="stat"><div className="num">50+</div><div className="lbl">Learning Paths</div></div>
                        <div className="stat"><div className="num">500+</div><div className="lbl">Curated Courses</div></div>
                        <div className="stat"><div className="num">3-12</div><div className="lbl">Months to Complete</div></div>
                        <div className="stat"><div className="num">100%</div><div className="lbl">Free to Start</div></div>
                    </StatsBand>

                    <SectionTitle>Popular Skill Paths</SectionTitle>
                    <Grid3>
                        {paths.map((p, i) => (
                            <Card key={i} bg={p.bg} color={p.color} onClick={() => history.push('/explore-courses')}>
                                <div className="card-icon">{p.icon}</div>
                                <h4>{p.title}</h4>
                                <p style={{ marginBottom: 8 }}><strong style={{ color: 'hsl(210,55%,22%)' }}>{p.steps}</strong></p>
                                <p>{p.desc}</p>
                                <span className="link">Start Path <MdArrowForward /></span>
                            </Card>
                        ))}
                    </Grid3>

                    <CtaBanner>
                        <div className="text">
                            <h2>Find Your Perfect Learning Path</h2>
                            <p>Structured skill paths designed to guide you efficiently toward your career goals.</p>
                        </div>
                        <div className="actions">
                            <button className="btn-white" onClick={() => history.push('/register')}>Start for Free</button>
                            <button className="btn-outline" onClick={() => history.push('/explore-courses')}>Browse All</button>
                        </div>
                    </CtaBanner>
                </Wrap>
            </PageBg>
        </PublicPageLayout>
    )
}

/* ══════════════════════════════════════════════════════
   PAGE: CORPORATE TRAINING  /corporate-training
══════════════════════════════════════════════════════ */
export const CorporateTrainingPage = () => {
    const history = useHistory()
    const perks = [
        { title: 'Unlimited Access for Teams', desc: 'Give your entire team unlimited access to our 4,000+ course library at zero cost.', icon: <MdPeople />, bg: '#e3f2fd', color: '#1565c0' },
        { title: 'Custom Learning Paths', desc: 'Work with our team to build custom training programs tailored to your company\'s needs.', icon: <MdSchool />, bg: '#e8f5e9', color: '#2e7d32' },
        { title: 'Progress Tracking & Reports', desc: 'Monitor team progress, completion rates, and skills acquired through a live dashboard.', icon: <MdAssessment />, bg: '#fff3e0', color: '#e65100' },
        { title: 'Certified Training Programs', desc: 'Issue company-recognized certificates to employees upon course completion.', icon: <MdCardMembership />, bg: '#f3e5f5', color: '#6a1b9a' },
        { title: 'Dedicated Success Manager', desc: 'Get assigned a dedicated training consultant to help roll out your program.', icon: <MdVerifiedUser />, bg: '#fce4ec', color: '#c62828' },
        { title: 'Fast Skill Development', desc: 'Employees gain job-critical skills faster with our bite-sized, focused learning modules.', icon: <MdFlightTakeoff />, bg: '#e8eaf6', color: '#283593' },
    ]
    return (
        <PublicPageLayout activeLink="courses">
            <PageBg>
                <Wrap>
                    <PageHero bg="/heroes/corporate-training.png">
                        <h1>Corporate Training Solutions</h1>
                        <p>Upskill your entire workforce with Wise Academy's enterprise training platform. Give your team access to 4,000+ courses, custom learning paths, and measurable skill development — all at no cost.</p>
                        <div className="cta-row">
                            <button className="btn-primary" onClick={() => history.push('/contact')}>Talk to Our Team</button>
                            <button className="btn-secondary" onClick={() => history.push('/register')}>Try It Free</button>
                        </div>
                    </PageHero>

                    <StatsBand>
                        <div className="stat"><div className="num">500+</div><div className="lbl">Organizations Served</div></div>
                        <div className="stat"><div className="num">15M+</div><div className="lbl">Employees Trained</div></div>
                        <div className="stat"><div className="num">4,000+</div><div className="lbl">Courses Available</div></div>
                        <div className="stat"><div className="num">100%</div><div className="lbl">Free LMS Included</div></div>
                    </StatsBand>

                    <SectionTitle>What's Included in Corporate Training</SectionTitle>
                    <Grid3>
                        {perks.map((p, i) => (
                            <FeatCard key={i}>
                                <div className="feat-icon" style={{ background: p.bg, color: p.color }}>{p.icon}</div>
                                <h4>{p.title}</h4>
                                <p>{p.desc}</p>
                            </FeatCard>
                        ))}
                    </Grid3>

                    <CtaBanner>
                        <div className="text">
                            <h2>Ready to Train Your Team?</h2>
                            <p>Book a free discovery call with our corporate training team and get started within 24 hours.</p>
                        </div>
                        <div className="actions">
                            <button className="btn-white" onClick={() => history.push('/contact')}>Book a Free Call</button>
                            <button className="btn-outline" onClick={() => history.push('/explore-courses')}>Browse Courses</button>
                        </div>
                    </CtaBanner>
                </Wrap>
            </PageBg>
        </PublicPageLayout>
    )
}

/* ══════════════════════════════════════════════════════
   PAGE: CATEGORY  /category/:slug
══════════════════════════════════════════════════════ */
const CATEGORY_DATA = {
    'computer-science': {
        name: 'Computer Science & IT', badge: <MdComputer style={{ marginRight: 6 }} />, badgeLabel: 'Technology', color: '#1565c0', bg: '#e3f2fd',
        desc: 'Master programming, web development, cloud computing, cybersecurity and more with our world-class IT courses designed for today\'s digital economy.',
        icon: <MdComputer />,
    },
    'health': {
        name: 'Health & Medicine', badge: <MdLocalHospital style={{ marginRight: 6 }} />, badgeLabel: 'Healthcare', color: '#c62828', bg: '#fce4ec',
        desc: 'Gain critical healthcare, nutrition, and medical skills from certified professionals. Prepare for a rewarding career in the health sector.',
        icon: <MdLocalHospital />,
    },
    'business': {
        name: 'Business & Finance', badge: <MdBusiness style={{ marginRight: 6 }} />, badgeLabel: 'Business', color: '#e65100', bg: '#fff3e0',
        desc: 'Learn entrepreneurship, financial management, strategic planning, and leadership skills to thrive in the modern business world.',
        icon: <MdBusiness />,
    },
    'marketing': {
        name: 'Sales & Marketing', badge: <MdTrendingUp style={{ marginRight: 6 }} />, badgeLabel: 'Marketing', color: '#2e7d32', bg: '#e8f5e9',
        desc: 'Master digital marketing, social media strategy, SEO, content marketing, and sales techniques to grow any business.',
        icon: <MdTrendingUp />,
    },
}

export const CategoryPage = ({ match }) => {
    const history = useHistory()
    const dispatch = useDispatch()
    const slug = match?.params?.slug || 'computer-science'
    const cat = CATEGORY_DATA[slug] || CATEGORY_DATA['computer-science']

    useEffect(() => {
        dispatch(getAllCourses())
    }, [dispatch])

    const { data: allCourses = [], loading } = useSelector(state => state.courses) || {}

    // Map slugs to backend categories
    const categoryMapping = {
        'computer-science': 'IT',
        'health': 'Health',
        'business': 'Business',
        'marketing': 'Marketing'
    }
    const backendCategory = categoryMapping[slug] || 'IT'

    // Filter courses matching this category
    const categoryCourses = allCourses?.filter(c => c.category === backendCategory && c.status !== 'archived') || []

    // Fall back to Mock Courses strictly if the backend has zero returned
    const displayCourses = categoryCourses.slice(0, 8)

    return (
        <PublicPageLayout activeLink="courses">
            <PageBg>
                <Wrap>
                    <PageHero bg={`/heroes/${slug === 'computer-science' ? 'computer-science' : slug === 'health' ? 'health' : slug === 'marketing' ? 'marketing' : 'business'}.png`}>
                        <h1>{cat.name} Courses</h1>
                        <p>{cat.desc}</p>
                        <div className="cta-row">
                            <button className="btn-primary" onClick={() => history.push('/explore-courses')}>Browse All Courses</button>
                            <button className="btn-secondary" onClick={() => history.push('/register')}>Enroll Free</button>
                        </div>
                    </PageHero>

                    <StatsBand>
                        <div className="stat"><div className="num">400+</div><div className="lbl">Courses Available</div></div>
                        <div className="stat"><div className="num">5M+</div><div className="lbl">Learners</div></div>
                        <div className="stat"><div className="num">100%</div><div className="lbl">Free to Start</div></div>
                    </StatsBand>

                    <SectionTitle>Popular {cat.name} Courses</SectionTitle>
                    {loading ? (
                        <div style={{ padding: '60px 0', textAlign: 'center' }}>
                            <Spinner size="large" />
                        </div>
                    ) : (
                        <Grid4>
                            {displayCourses.map((c, i) => (
                                <CourseCard key={c._id || c.id} onClick={() => history.push(c._id ? `/course/${c._id}` : '/register')}>
                                    <div className="thumb">
                                        <img src={c.image || c.img || THUMBS[i % THUMBS.length]} alt={c.name || c.title} />
                                    </div>
                                    <div className="body">
                                        <div className="title">{c.name || c.title}</div>
                                        <div className="stars">{[...Array(5)].map((_, si) => <MdStar key={si} />)}</div>
                                        <button className="btn">{c._id ? 'Explore Course' : 'Start Learning'}</button>
                                    </div>
                                </CourseCard>
                            ))}
                        </Grid4>
                    )}

                    <CtaBanner>
                        <div className="text">
                            <h2>Start Your {cat.name} Journey</h2>
                            <p>Join millions of learners worldwide. Free registration, expert instructors, recognized certificates.</p>
                        </div>
                        <div className="actions">
                            <button className="btn-white" onClick={() => history.push('/register')}>Get Started Free</button>
                        </div>
                    </CtaBanner>
                </Wrap>
            </PageBg>
        </PublicPageLayout>
    )
}

/* ══════════════════════════════════════════════════════
   PAGE: ABOUT / OUR MISSION  /about
══════════════════════════════════════════════════════ */
export const AboutPage = () => {
    const history = useHistory()
    const values = [
        { title: 'Access for All', desc: 'We believe quality education should be free and accessible to every person, everywhere, regardless of economic background.', icon: <MdPeople />, bg: '#e3f2fd', color: '#1565c0' },
        { title: 'Excellence First', desc: 'Every course on our platform is reviewed by experts to ensure the highest quality of learning content and outcomes.', icon: <MdVerifiedUser />, bg: '#e8f5e9', color: '#2e7d32' },
        { title: 'Real Career Impact', desc: 'We measure success by the career outcomes of our learners — jobs gained, promotions earned, lives transformed.', icon: <MdFlightTakeoff />, bg: '#fff8e1', color: '#f57f17' },
    ]
    return (
        <PublicPageLayout activeLink="about">
            <PageBg>
                <Wrap>
                    <PageHero bg="/heroes/about.png">
                        <h1>Our Mission: Education Without Barriers</h1>
                        <p>Wise Academy was founded on a single belief — that world-class education should be free, accessible, and meaningful for every person on the continent. From our roots in Rwanda, we're transforming lives across Africa through the power of online learning.</p>
                        <div className="cta-row">
                            <button className="btn-primary" onClick={() => history.push('/explore-courses')}>Start Learning Free</button>
                            <button className="btn-secondary" onClick={() => history.push('/success-stories')}>Read Our Stories</button>
                        </div>
                    </PageHero>

                    <StatsBand>
                        <div className="stat"><div className="num">2018</div><div className="lbl">Founded in Kigali</div></div>
                        <div className="stat"><div className="num">15M+</div><div className="lbl">Lives Impacted</div></div>
                        <div className="stat"><div className="num">50+</div><div className="lbl">Countries Reached</div></div>
                        <div className="stat"><div className="num">4,000+</div><div className="lbl">Free Courses</div></div>
                    </StatsBand>

                    <SectionTitle>Our Core Values</SectionTitle>
                    <Grid3>
                        {values.map((v, i) => (
                            <FeatCard key={i}>
                                <div className="feat-icon" style={{ background: v.bg, color: v.color }}>{v.icon}</div>
                                <h4>{v.title}</h4>
                                <p>{v.desc}</p>
                            </FeatCard>
                        ))}
                    </Grid3>

                    <CtaBanner>
                        <div className="text">
                            <h2>Join Our Mission</h2>
                            <p>Whether you're a learner, educator, or partner — there's a place for you at Wise Academy.</p>
                        </div>
                        <div className="actions">
                            <button className="btn-white" onClick={() => history.push('/register')}>Join for Free</button>
                            <button className="btn-outline" onClick={() => history.push('/contact')}>Partner With Us</button>
                        </div>
                    </CtaBanner>
                </Wrap>
            </PageBg>
        </PublicPageLayout>
    )
}

/* ══════════════════════════════════════════════════════
   PAGE: SUCCESS STORIES  /success-stories
══════════════════════════════════════════════════════ */
const StoryCard = styled.div`
  background: white; border-radius: 12px; padding: 32px; border: 1px solid #e5e7eb;
  transition: all 0.25s;
  &:hover { box-shadow: 0 12px 32px rgba(0,0,0,0.10); transform: translateY(-4px); }
  .avatar { width: 64px; height: 64px; border-radius: 50%; object-fit: cover; margin-bottom: 16px; }
  .quote { font-size: 15px; color: #374151; line-height: 1.7; margin-bottom: 20px; font-style: italic; }
  .stars { color: #f59e0b; font-size: 16px; margin-bottom: 12px; display: flex; gap: 2px; }
  .name { font-weight: 800; color: #111827; font-size: 15px; }
  .role { font-size: 13px; color: #6b7280; }
`

export const SuccessStoriesPage = () => {
    const history = useHistory()
    const stories = [
        { name: 'Marie Uwase', role: 'Lead Developer, MTN Rwanda', quote: '"The knowledge I gained from WISE Academy was instrumental. The curriculum\'s flexibility allowed me to study while working, and the certification led directly to my promotion."', img: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=200&q=80' },
        { name: 'Jean Baptiste', role: 'Data Analyst, Equity Bank', quote: '"I completed the Data Science path in 4 months while working full time. Three months later I had a new job with a 40% salary increase. WISE Academy changed my life."', img: 'https://images.unsplash.com/photo-1539701938214-0d9736e1c16b?w=200&q=80' },
        { name: 'Amalia Kagire', role: 'Marketing Manager, Kigali Startups', quote: '"The digital marketing courses were incredibly practical. I applied what I learned immediately and grew our company\'s online presence by 300% within 6 months."', img: 'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=200&q=80' },
        { name: 'Patrick Muneza', role: 'Nurse Practitioner, CHUK Hospital', quote: '"Studying healthcare management online allowed me to advance my nursing career without leaving my job. The quality of instruction was outstanding."', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80' },
        { name: 'Diane Mutesi', role: 'CEO, TechRwanda Ltd', quote: '"I built my entire startup using skills I learned for free on WISE Academy. From web development to business management — it was all there."', img: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&q=80' },
        { name: 'Eric Ntirenganya', role: 'Finance Director, RDB', quote: '"The financial accounting certificate course gave me the credibility I needed to step into a senior finance role. I am grateful for this free platform."', img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80' },
    ]
    return (
        <PublicPageLayout activeLink="about">
            <PageBg>
                <Wrap>
                    <PageHero bg="/heroes/success-stories.png">
                        <h1>Real People. Real Transformations.</h1>
                        <p>Meet the learners who used Wise Academy to land better jobs, launch businesses, and build the futures they dreamed of. Their stories prove that education truly has the power to change lives.</p>
                        <div className="cta-row">
                            <button className="btn-primary" onClick={() => history.push('/explore-courses')}>Start Your Story</button>
                        </div>
                    </PageHero>

                    <StatsBand>
                        <div className="stat"><div className="num">15M+</div><div className="lbl">Learners Worldwide</div></div>
                        <div className="stat"><div className="num">95%</div><div className="lbl">Employment Rate</div></div>
                        <div className="stat"><div className="num">50+</div><div className="lbl">Countries</div></div>
                    </StatsBand>

                    <SectionTitle>Alumni Success Stories</SectionTitle>
                    <Grid3>
                        {stories.map((s, i) => (
                            <StoryCard key={i}>
                                <img className="avatar" src={s.img} alt={s.name} />
                                <div className="stars">{[...Array(5)].map((_, si) => <MdStar key={si} />)}</div>
                                <p className="quote">{s.quote}</p>
                                <div className="name">{s.name}</div>
                                <div className="role">{s.role}</div>
                            </StoryCard>
                        ))}
                    </Grid3>

                    <CtaBanner>
                        <div className="text">
                            <h2>Write Your Own Success Story</h2>
                            <p>Join 15 million learners who've already transformed their careers with Wise Academy.</p>
                        </div>
                        <div className="actions">
                            <button className="btn-white" onClick={() => history.push('/register')}>Start for Free</button>
                        </div>
                    </CtaBanner>
                </Wrap>
            </PageBg>
        </PublicPageLayout>
    )
}

/* ══════════════════════════════════════════════════════
   PAGE: CONTACT  /contact
══════════════════════════════════════════════════════ */
const ContactGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 48px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`
const ContactForm = styled.div`
  background: white; border-radius: 12px; padding: 40px; border: 1px solid #e5e7eb;
  h3 { font-size: 20px; font-weight: 800; color: #111827; margin-bottom: 24px; }
  .form-group { margin-bottom: 20px; }
  label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
  input, textarea, select {
    width: 100%; padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 6px;
    font-size: 14px; outline: none; font-family: inherit;
    &:focus { border-color: hsl(210,55%,22%); box-shadow: 0 0 0 2px rgba(27,67,111,0.1); }
  }
  textarea { height: 120px; resize: vertical; }
  .submit-btn {
    width: 100%; height: 50px; background: hsl(33,100%,50%); border: none; border-radius: 6px;
    color: white; font-size: 15px; font-weight: 700; cursor: pointer; &:hover { opacity: 0.9; }
  }
`
const ContactInfo = styled.div`
  display: flex; flex-direction: column; gap: 20px;
  .info-card {
    background: white; border-radius: 12px; padding: 28px; border: 1px solid #e5e7eb;
    .info-icon { font-size: 28px; color: hsl(210,55%,22%); margin-bottom: 12px; }
    h4 { font-size: 16px; font-weight: 800; color: #111827; margin-bottom: 6px; }
    p { font-size: 14px; color: #6b7280; margin: 0; line-height: 1.6; }
  }
`

export const ContactPage = () => {
    return (
        <PublicPageLayout activeLink="about">
            <PageBg>
                <Wrap>
                    <PageHero bg="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1400&q=80">
                        <h1>Contact Wise Academy</h1>
                        <p>Have a question, partnership proposal, or need support? Our team is here to help. Reach out and we'll respond within 24 hours.</p>
                    </PageHero>

                    <ContactGrid>
                        <ContactForm>
                            <h3>Send Us a Message</h3>
                            <div className="form-group">
                                <label>Full Name</label>
                                <input type="text" placeholder="Your full name" />
                            </div>
                            <div className="form-group">
                                <label>Email Address</label>
                                <input type="email" placeholder="your@email.com" />
                            </div>
                            <div className="form-group">
                                <label>Subject</label>
                                <select>
                                    <option>General Inquiry</option>
                                    <option>Technical Support</option>
                                    <option>Partnership</option>
                                    <option>Corporate Training</option>
                                    <option>Press & Media</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Message</label>
                                <textarea placeholder="How can we help you?" />
                            </div>
                            <button className="submit-btn">Send Message</button>
                        </ContactForm>

                        <ContactInfo>
                            <div className="info-card">
                                <MdLocationOn className="info-icon" />
                                <h4>Our Office</h4>
                                <p>KG 7 Ave, <br />Kigali, Rwanda</p>
                            </div>
                            <div className="info-card">
                                <MdEmail className="info-icon" />
                                <h4>Email Us</h4>
                                <p>hello@wiseacademy.rw<br />wiseacademyrw@gmail.com</p>
                            </div>
                            <div className="info-card">
                                <MdPhone className="info-icon" />
                                <h4>Call Us</h4>
                                <p>+250 788 000 000<br />Mon – Fri, 8am – 6pm (CAT)</p>
                            </div>
                            <div className="info-card">
                                <MdChat className="info-icon" />
                                <h4>Live Chat</h4>
                                <p>Chat with our support team in real-time through the app or website.</p>
                            </div>
                        </ContactInfo>
                    </ContactGrid>
                </Wrap>
            </PageBg>
        </PublicPageLayout>
    )
}

/* ══════════════════════════════════════════════════════
   PAGE: COURSE PREVIEW  /course/:id
══════════════════════════════════════════════════════ */
const PreviewGrid = styled.div`
  display: grid; grid-template-columns: 2fr 1fr; gap: 32px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`
const SidebarCard = styled.div`
  background: white; border-radius: 12px; border: 1px solid #e5e7eb;
  padding: 24px; position: sticky; top: 100px;
  .price { font-size: 28px; font-weight: 800; color: #111827; margin-bottom: 20px; }
  .benefit { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #4b5563; margin-bottom: 12px; }
`

export const CoursePreviewPage = (props) => {
    const { id: paramId } = useParams()
    const id = paramId || props.match?.params?.id
    const history = useHistory()
    const dispatch = useDispatch()
    const [course, setCourse] = useState(null)
    const [loading, setLoading] = useState(true)

    const handleAddToCart = () => {
        if (course) {
            dispatch({
                type: 'ADD_TO_CART',
                data: {
                    id: course.id || course._id,
                    name: course.name,
                    image: course.image,
                    type: course.type || 'Certificate',
                    price: 0
                }
            })
            message.success(`${course.name} added to cart!`)
        }
    }

    useEffect(() => {
        const fetchCourse = async () => {
            try {
                const data = await courseService.getOneCourse(id)
                setCourse(data)
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        }
        fetchCourse()
    }, [id])

    if (loading) return <PublicPageLayout><div style={{ padding: 100, textAlign: 'center' }}><Spinner size="large" /></div></PublicPageLayout>
    if (!course) return <PublicPageLayout><div style={{ padding: 100, textAlign: 'center' }}>Course not found.</div></PublicPageLayout>

    return (
        <PublicPageLayout activeLink="courses">
            <PageBg>
                <Wrap>
                    <PageHero style={{ padding: '48px' }}>
                        <div style={{ display: 'flex', gap: '40px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: '300px' }}>
                                <h1 style={{ fontSize: '2.8rem' }}>{course.name}</h1>
                                <p style={{ fontSize: '1.2rem', marginBottom: '32px', color: '#666' }}>
                                    {course.description || `Master the skills of ${course.name} with this comprehensive, industry-aligned course.`}
                                </p>
                                <div style={{ display: 'flex', gap: '16px', marginBottom: '32px' }}>
                                    <button className="btn-primary" onClick={() => history.push('/register')}>Enroll Now</button>
                                    <button className="btn-secondary" onClick={handleAddToCart}>Add to Cart</button>
                                </div>
                                <div style={{ display: 'flex', gap: '24px', color: '#6b7280' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><MdStar style={{ color: '#f59e0b' }} /> 4.8 (2.4k ratings)</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><MdPeopleOutline /> {Math.floor(Math.random() * 10000) + 5000}+ Students</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><MdLanguage /> English, French</span>
                                </div>
                            </div>
                            <div style={{ width: '400px', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
                                <img src={course.image || THUMBS[0]} alt={course.name} style={{ width: '100%', display: 'block' }} />
                            </div>
                        </div>
                    </PageHero>

                    <PreviewGrid>
                        <div>
                            <SectionTitle>What you'll learn</SectionTitle>
                            <div style={{ background: 'white', padding: '32px', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '40px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                    {['Practical hands-on projects', 'Industry recognized methods', 'Advanced concepts & tools', 'Real-world case studies'].map((t, i) => (
                                        <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                            <MdCheckCircle style={{ color: '#059669', fontSize: '20px', flexShrink: 0 }} />
                                            <span style={{ color: '#4b5563' }}>{t}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <SectionTitle>Course Curriculum</SectionTitle>
                            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '40px' }}>
                                {course.modules && course.modules.length > 0 ? course.modules.map((m, i) => (
                                    <div key={i} style={{ padding: '20px 24px', borderBottom: i === course.modules.length - 1 ? 'none' : '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <MdPlayCircleOutline style={{ color: '#6b7280' }} />
                                            <span style={{ fontWeight: 600, color: '#111827' }}>Module {i + 1}: {m.title}</span>
                                        </div>
                                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>{m.moduleItems?.length || 0} Lessons</span>
                                    </div>
                                )) : (
                                    <div style={{ padding: '20px 24px', color: '#666' }}>No modules listed yet for this course.</div>
                                )}
                            </div>

                            <SectionTitle>About the Instructor</SectionTitle>
                            <div style={{ background: 'white', padding: '32px', borderRadius: '12px', border: '1px solid #e5e7eb', display: 'flex', gap: '24px' }}>
                                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#f3f4f6', overflow: 'hidden', flexShrink: 0 }}>
                                    <img src="https://i.pravatar.cc/150?u=instructor" alt="Instructor" />
                                </div>
                                <div>
                                    <h4 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '4px' }}>Dr. Sarah Mwizerwa</h4>
                                    <p style={{ color: '#2e7d32', fontWeight: 700, fontSize: '13px', marginBottom: '12px' }}>SENIOR INDUSTRY EXPERT</p>
                                    <p style={{ color: '#4b5563', lineHeight: 1.6 }}>
                                        Over 15 years of professional experience in {course.category || 'this field'}.
                                        Sarah has led major digital transformations across East Africa and is passionate about empowering the next generation of African professionals.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <SidebarCard>
                                <div className="price">Free Lifetime Access</div>
                                <Button
                                    type="primary"
                                    style={{ width: '100%', height: '54px', marginBottom: '16px', borderRadius: '6px', background: 'hsl(33, 100%, 50%)', borderColor: 'hsl(33, 100%, 50%)', fontWeight: 700, fontSize: '16px', boxShadow: 'none' }}
                                    onClick={() => history.push('/register')}
                                >
                                    Enroll for Free
                                </Button>
                                <Button
                                    ghost
                                    style={{ width: '100%', height: '54px', marginBottom: '24px', borderRadius: '6px', color: 'hsl(210, 55%, 22%)', borderColor: 'hsl(210, 55%, 22%)', fontWeight: 700 }}
                                    onClick={handleAddToCart}
                                >
                                    Add to Cart
                                </Button>
                                <p style={{ textAlign: 'center', fontSize: '13px', color: '#6b7280', marginBottom: '24px' }}>100% Free • Verified Certificate</p>

                                <Divider style={{ margin: '24px 0' }} />

                                <div className="benefit"><MdAccessTime /> {course.duration || '20'} hours of content</div>
                                <div className="benefit"><MdPlayCircleOutline /> 45 video lessons</div>
                                <div className="benefit"><MdComputer /> Access on mobile & desktop</div>
                                <div className="benefit"><MdCardMembership /> Verified Certificate</div>
                                <div className="benefit"><MdVerifiedUser /> Lifetime access</div>
                            </SidebarCard>
                        </div>
                    </PreviewGrid>
                </Wrap>
            </PageBg>
        </PublicPageLayout>
    )
}
